import axios from "axios";
import { useTranslation } from "react-i18next";
import type { DataMutate } from "../../../../types/types";
import { endpoints } from "../../../../endpoints";
import type {
  CollectionSchema,
  PIMProduct,
  PIMProductPartial,
  PIMProductStatusAck,
} from "../../../../types/types.PIM";
import {
  toTitleCase,
  useIsActylisStorefront,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../util/util";
import { useApprovalEligibilityContext } from "../Approvals/ApprovalEligibilityContext";
import { PROFILE_DETAIL_CLASSES } from "../../../../layout/shared/ProfileDetailLayout";
import {
  addRow,
  deleteRow,
  editCollectionRow,
  GENERATED_UUID,
} from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { ProductCollectionSchemaTable } from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchemaTable";
import type { OnSubmitValue } from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { CustomSKUConfig } from "./CustomSKUConfig";
import { useContext, useMemo, useState } from "react";
import { ObjectReferenceChangeConfirmDialog } from "../../../../components/ObjectReference/ObjectReferenceChangeConfirmDialog";
import {
  shouldConfirmObjectReferenceChange,
  shouldShowObjectReferenceAttribute,
} from "../../../../components/ObjectReference/utils";
import { buildCollectionPatchData } from "../ProductOverview/attributePatch.utils";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { OBJECT_REFERENCE_CONFIRMATION_PENDING } from "../../../../components/ObjectReference/utils";
import {
  applyPartialPimProductAndRefresh,
  revalidatePimProductDeferred,
} from "../../../../util/pimProductResponseMerge";
import { ProductContext } from "../ProductContext";
import { isDocumentAttribute } from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/documentAsset.utils";

type CollectionPatchValue = string | { display_text: string; url: string };

const mapCollectionValuesForRequest = (
  values: Array<{ attribute_id: string; value: unknown }>
): Array<{ attribute_id: string; value: CollectionPatchValue }> =>
  values.map(({ attribute_id, value }) => ({
    attribute_id,
    value: value as CollectionPatchValue,
  }));

async function syncAfterCollectionChange(
  ackedProduct: PIMProductStatusAck,
  partial: PIMProductPartial | undefined,
  productStatusBeforeEdit: string,
  fetchProductData: DataMutate<PIMProduct>,
  replaceProductId: (id: string) => void,
  triggerUpdate: () => Promise<void>,
  rowIdReplacement?: {
    from: string;
    to: string;
  }
): Promise<void> {
  const displayStatus = partial?.display_status ?? ackedProduct.display_status;
  const transitionedToStaged =
    (displayStatus === "staged" || displayStatus === "unpublished_staged") &&
    (productStatusBeforeEdit === "published" ||
      productStatusBeforeEdit === "unpublished");

  if (transitionedToStaged) {
    replaceProductId(
      partial?.product_number ??
        partial?.primary_staged_product_id ??
        partial?.id ??
        ackedProduct.product_number ??
        ackedProduct.primary_staged_product_id ??
        ackedProduct.id
    );
  } else if (partial) {
    await applyPartialPimProductAndRefresh(fetchProductData, partial, {
      rowIdReplacement,
    });
  } else {
    revalidatePimProductDeferred(fetchProductData);
  }

  void triggerUpdate();
}

export const CollectionComponent = ({
  collection: collectionProp,
  product: productProp,
  fetchProductData,
  isPortfolio = false,
  replaceProductId,
  isProductEntry = false,
}: {
  collection: CollectionSchema;
  product: PIMProduct;
  fetchProductData: DataMutate<PIMProduct>;
  replaceProductId: (id: string) => void;
  isPortfolio?: boolean;
  isProductEntry?: boolean;
}) => {
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const { t } = useTranslation();
  // seemlessly updates on SWR merge
  const product = useContext(ProductContext) ?? productProp;
  const collection = useMemo(() => {
    const fromProduct = product.product_schema.collections.find(
      (c) => c.id === collectionProp.id
    );
    if (!fromProduct) {
      return collectionProp;
    }
    if (isProductEntry) {
      return {
        ...fromProduct,
        columns: collectionProp.columns,
      };
    }
    return fromProduct;
  }, [collectionProp, isProductEntry, product.product_schema.collections]);
  const updateProductStatus = useUpdateProductStatus({ product });
  const { triggerUpdate } = useApprovalEligibilityContext();
  const { notifySuccess } = useNotifications();
  const [pendingObjectRefEdit, setPendingObjectRefEdit] =
    useState<{
      row_id: string;
      values: {
        attribute_id: string;
        value: string | { display_text: string; url: string } | null;
      }[];
      values_to_delete: string[];
      documentAssetIds: string[];
    } | null>(null);

  const attrMap = useMemo(
    () => new Map(collection.columns.map((attr) => [attr.id, attr])),
    [collection.columns]
  );

  const displayableColumns = useMemo(() => {
    const visibleColumns = isProductEntry
      ? collection.columns
      : !isPortfolio
      ? collection.columns.filter(({ is_visible }) => is_visible)
      : collection.columns.filter(
          ({ is_restricted, is_visible }) => !is_restricted && is_visible
        );

    return visibleColumns.filter((col) =>
      shouldShowObjectReferenceAttribute({
        inputType: col.input_type,
        objectReferenceEnabled: object_reference_enabled,
      })
    );
  }, [
    collection.columns,
    isPortfolio,
    isProductEntry,
    object_reference_enabled,
  ]);

  const isActylisStorefront = useIsActylisStorefront();

  // Staged products share the same AttributeValueRow UUIDs as the source product,
  // so the row_id remains valid after status transition.
  const getUpdatedProductRowId = (rowId: string) => {
    if (!collection.value_rows?.find(({ row_id }) => row_id === rowId)) {
      throw new Error(t("Could not find row id"));
    }
    return rowId;
  };

  // Update onEditRow to use the new function
  const onEditRow = async (targetRowValues: OnSubmitValue) => {
    const { row_id, ...formValue } = targetRowValues;
    collection.columns.forEach(({ id: attribute_id, is_not_applicable }) => {
      if (is_not_applicable) {
        delete formValue[attribute_id];
      }
    });
    const targetRow = collection.value_rows?.find(
      ({ row_id: cur_row_id }) => row_id === cur_row_id
    );

    const { values, values_to_delete } = buildCollectionPatchData(
      formValue,
      attrMap,
      targetRow
    );
    const requiresObjectRefConfirmation = Object.entries(formValue).some(
      ([attribute_id, value]) => {
        const attr = attrMap.get(attribute_id);
        if (attr?.input_type !== "object_reference") {
          return false;
        }

        const currentValues = (targetRow?.values ?? []).filter(
          (item) => item.attribute_id === attribute_id
        );
        return shouldConfirmObjectReferenceChange({
          existingValue: currentValues,
          nextFormValue: value,
        });
      }
    );
    try {
      if (!values.length && !values_to_delete.length) {
        return t("No changes to save");
      }
      if (requiresObjectRefConfirmation) {
        const pendingDocumentAssetIds = values
          .filter(({ attribute_id }) =>
            isDocumentAttribute(attrMap.get(attribute_id)?.input_type)
          )
          .map(({ value }) => (value as { asset_id?: string }).asset_id)
          .filter((id): id is string => Boolean(id));
        setPendingObjectRefEdit({
          row_id,
          values: mapCollectionValuesForRequest(values),
          values_to_delete,
          documentAssetIds: pendingDocumentAssetIds,
        });
        return OBJECT_REFERENCE_CONFIRMATION_PENDING;
      }
      const { data: ackedProduct } = await updateProductStatus();
      const { data } = await editCollectionRow({
        row_id: getUpdatedProductRowId(row_id),
        values: mapCollectionValuesForRequest(values),
        values_to_delete,
        tenant_id,
        product_id: ackedProduct.id,
        collection_id: collection.id,
      });
      const partial = data as PIMProductPartial;
      const updatedRowId =
        partial?.product_schema?.collections?.find(
          (c) => c.id === collection.id
        )?.value_rows?.[0]?.row_id ?? row_id;
      const newDocumentAssetIds = values
        .filter(({ attribute_id }) =>
          isDocumentAttribute(attrMap.get(attribute_id)?.input_type)
        )
        .map(({ value }) => (value as { asset_id?: string }).asset_id)
        .filter((id): id is string => Boolean(id));
      if (newDocumentAssetIds.length > 0) {
        await axios.patch(
          endpoints.v2_tenants_tenant_id_pim_products_product_id_assets_associate(
            tenant_id,
            ackedProduct.id
          ),
          { asset_ids: newDocumentAssetIds }
        );
      }
      await syncAfterCollectionChange(
        ackedProduct,
        partial,
        product.status,
        fetchProductData,
        replaceProductId,
        triggerUpdate,
        updatedRowId !== row_id
          ? {
              from: row_id,
              to: updatedRowId,
            }
          : undefined
      );
    } catch (error) {
      throw error;
    }
  };

  const submitConfirmedObjectRefEdit = async () => {
    if (!pendingObjectRefEdit) {
      return;
    }
    try {
      const { row_id, values, values_to_delete, documentAssetIds } =
        pendingObjectRefEdit;
      const { data: ackedProduct } = await updateProductStatus();
      const { data } = await editCollectionRow({
        row_id: getUpdatedProductRowId(row_id),
        values,
        values_to_delete,
        tenant_id,
        product_id: ackedProduct.id,
        collection_id: collection.id,
      });
      const partial = data as PIMProductPartial;
      const updatedRowId =
        partial?.product_schema?.collections?.find(
          (c) => c.id === collection.id
        )?.value_rows?.[0]?.row_id ?? row_id;
      if (documentAssetIds.length > 0) {
        await axios.patch(
          endpoints.v2_tenants_tenant_id_pim_products_product_id_assets_associate(
            tenant_id,
            ackedProduct.id
          ),
          { asset_ids: documentAssetIds }
        );
      }
      await syncAfterCollectionChange(
        ackedProduct,
        partial,
        product.status,
        fetchProductData,
        replaceProductId,
        triggerUpdate,
        updatedRowId !== row_id
          ? {
              from: row_id,
              to: updatedRowId,
            }
          : undefined
      );
      notifySuccess(t("Your changes have been saved successfully"));
    } finally {
      setPendingObjectRefEdit(null);
    }
  };

  // Update onAddRow to use the new function
  const onAddRow = async (rowFormValues: OnSubmitValue) => {
    const { row_id, ...formValues } = rowFormValues;
    collection.columns.forEach(({ id, is_not_applicable }) => {
      if (is_not_applicable) {
        delete rowFormValues[id];
      }
    });

    const { values } = buildCollectionPatchData(formValues, attrMap);
    if (!values.length) {
      return t("No changes to save");
    }
    try {
      const { data: ackedProduct } = await updateProductStatus();
      const { data } = await addRow({
        values: mapCollectionValuesForRequest(values),
        tenant_id,
        product_id: ackedProduct.id,
        collection_id: collection.id,
      });
      const newDocumentAssetIds = values
        .filter(({ attribute_id }) =>
          isDocumentAttribute(attrMap.get(attribute_id)?.input_type)
        )
        .map(({ value }) => (value as { asset_id?: string }).asset_id)
        .filter((id): id is string => Boolean(id));
      if (newDocumentAssetIds.length > 0) {
        await axios.patch(
          endpoints.v2_tenants_tenant_id_pim_products_product_id_assets_associate(
            tenant_id,
            ackedProduct.id
          ),
          { asset_ids: newDocumentAssetIds }
        );
      }
      await syncAfterCollectionChange(
        ackedProduct,
        data as PIMProductPartial,
        product.status,
        fetchProductData,
        replaceProductId,
        triggerUpdate
      );
    } catch (error) {
      throw error;
    }
  };

  const onDeleteRow = async (row_id: string) => {
    try {
      const { data: ackedProduct } = await updateProductStatus();
      await deleteRow({
        tenant_id,
        collection_id: collection.id,
        product_id: ackedProduct.id,
        row_id: getUpdatedProductRowId(row_id),
      });
      await syncAfterCollectionChange(
        ackedProduct,
        undefined,
        product.status,
        fetchProductData,
        replaceProductId,
        triggerUpdate
      );
    } catch (error) {
      throw error;
    }
  };

  const onRowSubmit = async (values: OnSubmitValue) => {
    if (values.row_id.includes(GENERATED_UUID)) {
      return onAddRow(values);
    } else {
      return onEditRow(values);
    }
  };

  const isSKUCollection = collection.object_type === "skus";
  const canShowSKUConfig = isSKUCollection && !isPortfolio;

  if (!displayableColumns.length) {
    return null;
  }

  return (
    <>
      <div className={PROFILE_DETAIL_CLASSES.collectionCard}>
        <ProductCollectionSchemaTable
          collectionSchema={collection}
          barentzApiBatches={product.barentz_api_batches}
          title={toTitleCase(
            collection.display_name
              ? t([collection.display_name])
              : t(collection.name)
          )}
          onSubmitForm={onRowSubmit}
          onDeleteRow={onDeleteRow}
          showMandatoryAsterisk={
            product.product_schema.show_mandatory_completion_score &&
            !isPortfolio
          }
          fetchProductData={fetchProductData}
          isPortfolio={isPortfolio}
          productID={product.id}
          productStatus={product.status}
          isEditable={product.is_editable}
          isLockedForEdit={product.is_locked}
          pimProductType={product.pim_product_type}
          productOwner={product.product_owner}
          isProductEntry={isProductEntry}
        />
      </div>
      {canShowSKUConfig && !isActylisStorefront && (
        <div className={PROFILE_DETAIL_CLASSES.collectionCard}>
          <CustomSKUConfig
            allow_custom_sku={product.allow_custom_sku}
            replaceProductId={replaceProductId}
            updateProductStatus={updateProductStatus}
            fetchProductData={fetchProductData}
            productIsEditable={
              product.is_editable && product.status !== "archived"
            }
            productIsLockedForEdit={product.is_locked}
          />
        </div>
      )}
      {pendingObjectRefEdit && (
        <ObjectReferenceChangeConfirmDialog
          show={true}
          onClose={() => setPendingObjectRefEdit(null)}
          onConfirm={submitConfirmedObjectRefEdit}
        />
      )}
    </>
  );
};
